import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import styles from './TextToJsonTool.module.css';

const MODE_TEXT = 'text';
const MODE_FILE = 'file';
const MODE_BATCH = 'batch';
const BATCH_REQUEST_TIMEOUT = 3 * 60 * 1000;

function TextToJsonTool() {
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const [selectedMode, setSelectedMode] = useState(MODE_TEXT);
  const [selectedFile, setSelectedFile] = useState(null);
  const [batchDirectory, setBatchDirectory] = useState('');
  const [volumes, setVolumes] = useState([]);
  const [volumesLoading, setVolumesLoading] = useState(false);
  const [volumesError, setVolumesError] = useState('');
  const [selectedVolumeId, setSelectedVolumeId] = useState('');
  const [batchResult, setBatchResult] = useState(null);

  const backendBaseUrl = useMemo(() => {
    const baseUrl = api.defaults.baseURL || '';
    if (!baseUrl) {
      return '';
    }
    const trimmed = baseUrl.replace(/\/?$/, '');
    return trimmed.replace(/\/api$/, '');
  }, []);

  const isFileMode = selectedMode === MODE_FILE;
  const isBatchMode = selectedMode === MODE_BATCH;
  const savedUrlValue = isBatchMode ? '' : result?.saved_url || '';

  const resolvedSavedUrl = useMemo(() => {
    if (!savedUrlValue) {
      return '';
    }
    if (/^https?:\/\//.test(savedUrlValue)) {
      return savedUrlValue;
    }
    return backendBaseUrl ? `${backendBaseUrl}${savedUrlValue}` : savedUrlValue;
  }, [savedUrlValue, backendBaseUrl]);

  const formatFileSize = (bytes) => {
    if (!Number.isFinite(bytes)) {
      return '';
    }
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
    if (bytes >= 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${bytes} B`;
  };

  const loadVolumes = useCallback(async () => {
    setVolumesLoading(true);
    setVolumesError('');
    try {
      const { data } = await api.get('/volumes/', { params: { limit: 500 } });
      const list = Array.isArray(data) ? data : [];
      setVolumes(list);
      setSelectedVolumeId((prev) => {
        if (prev && list.some((item) => String(item.id) === String(prev))) {
          return prev;
        }
        return list.length > 0 ? String(list[0].id) : '';
      });
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        '获取卷列表失败，请稍后重试';
      setVolumesError(message);
    } finally {
      setVolumesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isBatchMode) {
      return;
    }
    if (volumes.length === 0 && !volumesLoading) {
      loadVolumes();
    }
  }, [isBatchMode, volumes.length, volumesLoading, loadVolumes]);

  const renderVolumeOptionLabel = (volume) => {
    if (!volume) {
      return '';
    }
    const pieces = [];
    if (volume.novel?.title) {
      pieces.push(volume.novel.title);
    }
    pieces.push(volume.title);
    if (typeof volume.order_index === 'number') {
      pieces.push(`#${volume.order_index}`);
    }
    return pieces.join(' / ');
  };

  const handleModeChange = (mode) => {
    if (mode === selectedMode) {
      return;
    }
    setSelectedMode(mode);
    setError('');
    setCopyStatus('');
    setResult(null);
    setBatchResult(null);
    if (mode === MODE_TEXT) {
      setSelectedFile(null);
      setBatchDirectory('');
    } else if (mode === MODE_FILE) {
      setText('');
      setBatchDirectory('');
    } else if (mode === MODE_BATCH) {
      setSelectedFile(null);
      setText('');
      setCopyStatus('');
      if (!volumesLoading && volumes.length === 0) {
        loadVolumes();
      }
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    setSelectedFile(file);
    setError('');
    setCopyStatus('');
    setResult(null);
    setBatchResult(null);
    if (!file) {
      return;
    }
    if (file.size === 0) {
      setError('文件为空，请选择有效的TXT文件');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setCopyStatus('');
    setResult(null);
    setBatchResult(null);

    if (isBatchMode) {
      if (!selectedVolumeId) {
        setError('请选择要导入的卷');
        return;
      }

      const trimmedDirectory = batchDirectory.trim();
      if (!trimmedDirectory) {
        setError('请输入包含 TXT 文件的文件夹路径');
        return;
      }

      try {
        setIsSubmitting(true);
        const { data } = await api.post(
          '/text-to-json/batch',
          {
            volume_id: Number(selectedVolumeId),
            directory_path: trimmedDirectory,
          },
          { timeout: BATCH_REQUEST_TIMEOUT },
        );
        setBatchResult(data);
      } catch (err) {
        const message =
          err?.response?.data?.detail ||
          err?.message ||
          '批量转换失败，请稍后重试';
        setError(`批量转换失败：${message}`);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (isFileMode) {
      if (!selectedFile) {
        setError('请选择要上传的TXT文件');
        return;
      }
      if (selectedFile.size === 0) {
        setError('文件为空，请重新选择TXT文件');
        return;
      }

      const formData = new FormData();
      formData.append('file', selectedFile);

      try {
        setIsSubmitting(true);
        const { data } = await api.post('/text-to-json/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setResult(data);
      } catch (err) {
        const message =
          err?.response?.data?.detail ||
          err?.message ||
          '转换失败，请稍后重试';
        setError(`转换失败：${message}`);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    const normalizedText = text.trim();
    if (!normalizedText) {
      setError('请输入章节原文内容');
      return;
    }

    const payload = {
      text: normalizedText,
    };

    try {
      setIsSubmitting(true);
      const { data } = await api.post('/text-to-json/', payload);
      setResult(data);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        '转换失败，请稍后重试';
      setError(`转换失败：${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyJson = async () => {
    if (!result) {
      return;
    }

    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard) {
        setCopyStatus('浏览器不支持剪贴板复制');
        return;
      }

      await navigator.clipboard.writeText(
        JSON.stringify(result, null, 2),
      );
      setCopyStatus('已复制到剪贴板');
    } catch (err) {
      setCopyStatus('复制失败，请手动复制');
    }
  };

  const handleDownloadJson = () => {
    if (!result) {
      return;
    }

    if (
      typeof window === 'undefined' ||
      !window.URL ||
      typeof window.URL.createObjectURL !== 'function'
    ) {
      setCopyStatus('当前环境不支持文件下载');
      return;
    }

    const payload = JSON.stringify(result, null, 2);
    const blob = new Blob([payload], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const deriveBaseName = () => {
      if (result.saved_url) {
        const segments = result.saved_url.split('/');
        const last = segments[segments.length - 1] || '';
        if (last) {
          return last.replace(/\.json$/i, '');
        }
      }
      if (selectedFile?.name) {
        return selectedFile.name.replace(/\.txt$/i, '');
      }
      if (result.chapter_title) {
        return result.chapter_title;
      }
      return 'chapter';
    };

    const sanitizedBase = deriveBaseName()
      .replace(/[\\s/\\\\:*?"<>|]+/g, '_')
      .slice(0, 80)
      .replace(/^_+/, '');
    const fileName = `${sanitizedBase || 'chapter'}.json`;

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.textToJson}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>文本转 JSON</h1>
          <p className={styles.subtitle}>
            输入整章文本或上传TXT文件，快速拆分为章节句子结构数据。
          </p>
        </div>
      </header>

      <section className={styles.panel}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.modeSwitch}>
            <button
              type="button"
              className={`${styles.modeButton} ${
                selectedMode === MODE_TEXT ? styles.modeButtonActive : ''
              }`}
              onClick={() => handleModeChange(MODE_TEXT)}
            >
              粘贴文本
            </button>
            <button
              type="button"
              className={`${styles.modeButton} ${
                selectedMode === MODE_FILE ? styles.modeButtonActive : ''
              }`}
              onClick={() => handleModeChange(MODE_FILE)}
            >
              上传 TXT 文件
            </button>
            <button
              type="button"
              className={`${styles.modeButton} ${
                selectedMode === MODE_BATCH ? styles.modeButtonActive : ''
              }`}
              onClick={() => handleModeChange(MODE_BATCH)}
            >
              批量导入文件夹
            </button>
          </div>

          {isBatchMode ? (
            <div className={styles.formRow}>
              <p className={styles.hint}>
                选择目标卷并指定服务器上的 TXT 文件夹。每个文件名需以数字开头表示章节排序，例如
                <code>0013_第13章.txt</code>，数字用于 order_index，后半部分作为章节标题。
              </p>
            </div>
          ) : (
            <div className={styles.formRow}>
              <p className={styles.hint}>
                文本需以前三行提供章节信息，格式示例：
                <br />
                第180章 善恶道 (3)
                <br />
                原始链接: https://www.kanunu8.com/book2/10914/194822.html
                <br />
                所属分卷: 最终卷
              </p>
            </div>
          )}

          {isBatchMode ? (
            <>
              <div className={styles.formRow}>
                <label className={styles.label} htmlFor="volumeSelect">
                  目标卷
                </label>
                <div className={styles.inlineFields}>
                  <select
                    id="volumeSelect"
                    className={styles.input}
                    value={selectedVolumeId}
                    onChange={(event) => setSelectedVolumeId(event.target.value)}
                    disabled={volumesLoading || volumes.length === 0}
                  >
                    {volumes.length === 0 ? (
                      <option value="">暂无可用卷</option>
                    ) : (
                      volumes.map((volume) => (
                        <option key={volume.id} value={volume.id}>
                          {renderVolumeOptionLabel(volume)}
                        </option>
                      ))
                    )}
                  </select>
                  <button
                    type="button"
                    className={styles.modeButton}
                    onClick={loadVolumes}
                    disabled={volumesLoading}
                  >
                    {volumesLoading ? '加载中...' : '刷新卷列表'}
                  </button>
                </div>
                {volumesError && <div className={styles.error}>{volumesError}</div>}
                {volumesLoading && !volumesError && (
                  <p className={styles.hint}>卷列表加载中，请稍候…</p>
                )}
              </div>

              <div className={styles.formRow}>
                <label className={styles.label} htmlFor="directoryPath">
                  TXT 文件夹路径
                </label>
                <input
                  id="directoryPath"
                  className={styles.input}
                  type="text"
                  value={batchDirectory}
                  onChange={(event) => setBatchDirectory(event.target.value)}
                  placeholder="例如：/data/novels/volume_01"
                />
                <p className={styles.hint}>
                  文件夹内应仅包含要导入的 .txt 文件，系统会按文件名前缀的数字顺序处理。
                </p>
              </div>
            </>
          ) : isFileMode ? (
            <div className={styles.formRow}>
              <label className={styles.label} htmlFor="chapterFile">
                TXT 文件
              </label>
              <input
                id="chapterFile"
                className={styles.fileInput}
                type="file"
                accept=".txt,text/plain"
                onChange={handleFileChange}
              />
              {selectedFile && (
                <p className={styles.fileInfo}>
                  已选择：{selectedFile.name}（{formatFileSize(selectedFile.size)}）
                </p>
              )}
              <p className={styles.hint}>
                支持 .txt 纯文本文件，系统会根据常见编码自动解码内容。
              </p>
            </div>
          ) : (
            <div className={styles.formRow}>
              <label className={styles.label} htmlFor="chapterText">
                章节原文
              </label>
              <textarea
                id="chapterText"
                className={styles.textarea}
                placeholder="将整章文本粘贴在此处，工具会自动按句号、问号、感叹号分句。"
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
              <p className={styles.hint}>
                支持中英文混合，每个句子会自动编号并保留原始标点。
              </p>
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.actions}>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={
                isSubmitting ||
                (isBatchMode && (volumesLoading || volumes.length === 0 || !selectedVolumeId))
              }
            >
              {isSubmitting
                ? isBatchMode
                  ? '批量导入中...'
                  : '转换中...'
                : isBatchMode
                ? '批量导入章节'
                : isFileMode
                ? '上传并转换'
                : '开始转换'}
            </button>
          </div>
        </form>
      </section>

      {!isBatchMode && result && (
        <section className={styles.resultSection}>
          <div className={styles.resultHeader}>
            <div>
              <h2 className={styles.resultTitle}>转换结果</h2>
              <p className={styles.resultSummary}>
                共生成 {result.sentence_count} 条句子数据
              </p>
            </div>
            <div className={styles.resultActions}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={handleCopyJson}
              >
                复制 JSON
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={handleDownloadJson}
              >
                下载文件
              </button>
            </div>
          </div>

          {copyStatus && (
            <div className={styles.copyStatus}>{copyStatus}</div>
          )}

          <div className={styles.metadata}>
            {result.chapter_title && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>章节标题</span>
                <span className={styles.metaValue}>{result.chapter_title}</span>
              </div>
            )}
            {result.original_link && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>原始链接</span>
                {/^https?:\/\//.test(result.original_link) ? (
                  <a
                    className={styles.metaLink}
                    href={result.original_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {result.original_link}
                  </a>
                ) : (
                  <span className={styles.metaValue}>
                    {result.original_link}
                  </span>
                )}
              </div>
            )}
            {result.volume && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>所属卷</span>
                <span className={styles.metaValue}>{result.volume}</span>
              </div>
            )}
            {result.saved_path && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>保存路径</span>
                <span className={styles.metaValue}>{result.saved_path}</span>
              </div>
            )}
            {resolvedSavedUrl && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>下载链接</span>
                <a
                  className={styles.metaLink}
                  href={resolvedSavedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {resolvedSavedUrl}
                </a>
              </div>
            )}
          </div>

          <div className={styles.preview}>
            <h3 className={styles.previewTitle}>JSON 预览</h3>
            <pre className={styles.previewContent}>
{JSON.stringify(result, null, 2)}
            </pre>
          </div>

          {Array.isArray(result.sentences) && result.sentences.length > 0 && (
            <div className={styles.sentences}>
              <h3 className={styles.previewTitle}>句子预览（前 10 条）</h3>
              <ul className={styles.sentenceList}>
                {result.sentences.slice(0, 10).map((item) => (
                  <li key={item.sentence_id} className={styles.sentenceItem}>
                    <span className={styles.sentenceId}>
                      #{item.sentence_id}
                    </span>
                    <span className={styles.sentenceContent}>
                      {item.sentence_content}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {isBatchMode && batchResult && (
        <section className={styles.resultSection}>
          <div className={styles.resultHeader}>
            <div>
              <h2 className={styles.resultTitle}>批量导入结果</h2>
              <p className={styles.resultSummary}>
                已处理 {batchResult.processed_files} / {batchResult.total_files} 个文件
              </p>
            </div>
          </div>

          <div className={styles.metadata}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>目标卷 ID</span>
              <span className={styles.metaValue}>{batchResult.volume_id}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>TXT 文件夹</span>
              <span className={styles.metaValue}>{batchResult.directory}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>新增章节</span>
              <span className={styles.metaValue}>{batchResult.created}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>更新章节</span>
              <span className={styles.metaValue}>{batchResult.updated}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>失败文件</span>
              <span className={styles.metaValue}>{batchResult.failed}</span>
            </div>
          </div>

          {Array.isArray(batchResult.results) && batchResult.results.length > 0 && (
            <div className={styles.tableWrapper}>
              <table className={styles.resultTable}>
                <thead>
                  <tr>
                    <th>文件名</th>
                    <th>顺序</th>
                    <th>标题</th>
                    <th>句子数</th>
                    <th>状态</th>
                    <th>详情</th>
                  </tr>
                </thead>
                <tbody>
                  {batchResult.results.map((item, index) => {
                    const statusClass =
                      item.status === 'created'
                        ? styles.statusCreated
                        : item.status === 'updated'
                        ? styles.statusUpdated
                        : item.status === 'failed'
                        ? styles.statusFailed
                        : '';
                    return (
                      <tr key={`${item.filename}-${index}`}>
                        <td>{item.filename}</td>
                        <td>
                          {typeof item.order_index === 'number'
                            ? item.order_index
                            : '-'}
                        </td>
                        <td>{item.title || '-'}</td>
                        <td>
                          {typeof item.sentence_count === 'number'
                            ? item.sentence_count
                            : '-'}
                        </td>
                        <td className={statusClass}>{item.status}</td>
                        <td>{item.detail || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default TextToJsonTool;
